import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import Handle from '~/prix/react/components/handle'
import Modal from '~/prix/react/components/modal'
import useAPI from '~/prix/react/hooks/api'
import recommendedLayerForVariable from '~/packages/general/variable/recommendedLayerForVariable.util'
import { Layer } from '~/packages/general/variable/spaceTime.types'

import AskPrixAiEmptyState from '~/components/llm/askPrixAiEmptyState'
import buildVariableSearchQuestion from '~/components/llm/buildVariableSearchQuestion'
import useAskPrixAi from '~/components/llm/hooks/useAskPrixAi'
import VariableSearchMatchBanner from '~/components/llm/variableSearchMatchBanner'
import { NotFoundInfo } from '~/components/mapContent/styles/modalSelectVariable'
import categories from '~/packages/categories'
import ScrollList from './scrollList'
import { faList } from '@fortawesome/free-solid-svg-icons'
import WrapperFilters from './categoryFilter'
import { useMedia } from 'react-use'
import { Expression } from '~/prix/types/expression'

import { useLocation, useNavigate } from 'react-router'
import { ToolsAndOptionalResult } from '~/packages/general/variable/tools.typing'
import { BaseWrapper, WrapperButtons } from '~/components/mapContent/styles/modals'

import InputSearch from '~/components/inputSearch'
import { ButtonVariants } from '~/components/buttonVariants'
import { ZodSchemaInfo } from '~/prix/types/zodType'
import useVariableListByCategory from '~/components/utils/hooks/useVariableListByCategory'
import { ViewListItem } from '~/packages/general/view/list/viewList.typing'

const PAGE_SIZE = 50

const WrapperModalVariable = styled(BaseWrapper).attrs(() => ({
  $flexDirection: 'row',
  $height: '72vh',
  $justifyLeftContent: 'space-around',
}))`
  .content {
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;
  }
  .filters {
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
  }

  .filter-category {
    display: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
      display: flex;
      width: 15%;
      border-radius: 8px;
      justify-content: center;
      align-items: center;
    }
  }
`

const InputSearchStyled = styled(InputSearch)`
  background-color: ${({ theme }) => theme.colors.lightGrey};
  border: none;
  border-radius: 0;
  border-top-right-radius: ${({ theme }) => theme.borderRadius};
  outline: none;
  padding: ${({ theme }) => theme.padding(2)};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    border-top-left-radius: ${({ theme }) => theme.borderRadius};
    border-top-right-radius: 0;
  }
`

const Variables = styled(BaseWrapper)`
  flex: 1;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    min-height: 56dvh;
  }

  .variable {
    display: flex;
    position: relative;
    flex-direction: column;
    justify-content: space-between;
    gap: 3px;
    padding: ${({ theme }) => theme.padding(2)};
    cursor: pointer;

    &::before {
      content: '';
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      margin-left: ${({ theme }) => theme.padding(2)};
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.selectedItem};
    }

    &.is-selected,
    &.is-selected:hover {
      background-color: ${({ theme }) => theme.colors.lightGrey};
    }

    .alert {
      padding: ${({ theme }) => theme.padding(1)} ${({ theme }) => theme.padding(2)};
      border-radius: ${({ theme }) => theme.borderRadius};
      margin-right: ${({ theme }) => theme.padding(1)};
      text-transform: uppercase;
      font-weight: bold;
      white-space: nowrap;
    }
  }
`

interface ModalSelectVariableProps {
  isOpen: boolean
  onClose: () => void
  layer: Layer
  baseLayer: Layer
  onChangeLayer: (layer: Layer | ((oldLayer: Layer) => Layer)) => void
  disableNavigation?: boolean
}
export default function ModalSelectVariable({
  isOpen,
  onClose,
  layer,
  baseLayer,
  onChangeLayer,
  disableNavigation = false,
}: ModalSelectVariableProps) {
  const isMobile = useMedia('(max-width: 768px)')
  const { context, isLoading: isLoadingAPI } = useAPI()

  // Estado da variável selecionada
  const [currentBaseLayer, setCurrentBaseLayer] = useState<Layer>(baseLayer)
  const [currentCategory, setCurrentCategory] = useState<typeof categories>({
    all: { title: 'Tudo', sectorKey: 'general' },
  })

  // Busca
  const [isDebouncing, setIsDebouncing] = useState(false)
  const [search, setSearch] = useState('')

  const [hasChangedVariableLayer, setHasChangedVariableLayer] = useState(false)

  // Filtros (expressões)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [filters, setFilters] = useState<Expression[] | undefined>([])
  const [filterableBy, setFilterableBy] = useState<ZodSchemaInfo | undefined>()
  const filterRef = useRef<Expression[] | undefined>([])

  // Verifica se a variável é uma ferramenta
  const hasPath = useMemo(() => {
    return !!(currentBaseLayer.variable as ToolsAndOptionalResult).path
  }, [currentBaseLayer])

  const navigate = useNavigate()
  const location = useLocation()
  const { askPrixAi, isLoading: isOpeningPrixAi } = useAskPrixAi()

  const { fetchVariables, variables, pagesLeft, isLoading } = useVariableListByCategory()
  const nextStartIndexRef = useRef<number | null>(null)

  const categoryKey = useMemo(() => {
    return currentCategory && Object.keys(currentCategory)[0]
  }, [currentCategory])

  const currentCategoryInfo = useMemo(() => {
    return {
      key: categoryKey,
      title: currentCategory[categoryKey]?.title,
      sectorKey: currentCategory[categoryKey]?.sectorKey,
    }
  }, [categoryKey, currentCategory])

  const cleanLoadingState = () => {
    setHasChangedVariableLayer(false)
  }

  useEffect(() => {
    if (!isOpen) {
      return
    }

    void fetchVariables(0, currentCategoryInfo, 'relevance', PAGE_SIZE, search, true).finally(() => {
      cleanLoadingState()
      nextStartIndexRef.current = null
    })
  }, [currentCategoryInfo, fetchVariables, isOpen, search])

  const filteredVariables: ViewListItem[] = useMemo(() => {
    return variables.filter(variable => variable.isListed !== false)
  }, [variables])

  useEffect(() => {
    setCurrentBaseLayer(baseLayer)
  }, [baseLayer])

  const getFilter = useCallback((expressions: Record<number, Expression> | undefined) => {
    filterRef.current = expressions ? Object.values(expressions) : []
    setFilters(filterRef.current)
  }, [])

  // Seleciona a variável e abre opções de filtro
  const handleSelectVariable = useCallback(
    (variable: ViewListItem) => {
      if (!context.user) {
        return
      }

      const currentBase = currentBaseLayer?.variable
      if (currentBase?.key !== variable.key || currentBase?.presetKey !== variable.presetKey) {
        setFilters([])
        filterRef.current = []
      }
      setCurrentBaseLayer(current => recommendedLayerForVariable(variable, context.user!, current))

      const filterableBy = (variable.filterableBy as { shape?: ZodSchemaInfo } | undefined)?.shape
      setFilterableBy(filterableBy)
    },
    [context.user, currentBaseLayer],
  )

  // Navega para a página da tool selecionada
  const handleNavigation = () => {
    if (disableNavigation) {
      return
    }

    const state = {
      replace: true,
      state: {
        currentBaseLayer,
      },
    }
    if (!hasPath && location.pathname !== '/') {
      navigate(`/`, state)
    } else {
      const path = (currentBaseLayer.variable as ToolsAndOptionalResult).path
      if (path && location.pathname !== path) {
        navigate(path, state)
      }
    }
  }

  // Cancela a seleção de variáveis
  // e retorna ao estado prévio
  const handleCancel = () => {
    onClose()
    setCurrentBaseLayer(layer)
    handleSearch('')
  }

  // Confirmação de escolha e filtro de variáveis
  const handleConfirm = () => {
    const expressions = filterRef?.current || []

    const validFilters = expressions.filter(
      filter => filter?.kind === 'binary' && filter?.right && Object.keys(filter?.right).length > 0,
    )

    const interleavedAdditionalFilters = validFilters.reduce((acc, filter, index) => {
      if (index === 0) {
        return filter
      }
      return {
        type: 'expression',
        kind: 'binary',
        left: acc,
        operator: 'AND',
        right: filter,
      }
    }, {})
    const withFilters = {
      ...currentBaseLayer,
      variable: {
        ...currentBaseLayer.variable,
        additionalFilters:
          Object.keys(interleavedAdditionalFilters).length > 0
            ? (interleavedAdditionalFilters as Expression)
            : undefined,
      },
    }
    setFilters(validFilters)
    if (Object.keys(interleavedAdditionalFilters).length > 0 && !hasPath) {
      onChangeLayer(withFilters)
    } else {
      onChangeLayer(currentBaseLayer)
    }

    handleNavigation()
    onClose()
  }

  const categoriesEntries = Object.entries(categories)
  const categoriesInfo = [
    ...categoriesEntries.map(([key, value]) => ({
      key,
      title: value.title,
      sectorKey: value.sectorKey,
    })),
  ]

  // Mudança de categoria
  const handleClickCategory = (info: { key: string; title: string; sectorKey: string }) => {
    setSearch('')
    setHasChangedVariableLayer(true)
    setCurrentCategory({
      [info.key]: {
        title: info.title,
        sectorKey: info.sectorKey,
      },
    })
  }

  const handleSearch = useCallback(
    (value: string) => {
      if (value !== search) {
        setSearch(value)
        setHasChangedVariableLayer(true)
      }
    },
    [search, setSearch, setHasChangedVariableLayer],
  )

  const handleLoadMoreItems = useCallback(async () => {
    if (isLoading || pagesLeft <= 0) {
      return
    }

    const startIndex = filteredVariables.length
    if (nextStartIndexRef.current === startIndex) {
      return
    }

    nextStartIndexRef.current = startIndex

    try {
      await fetchVariables(startIndex, currentCategoryInfo, 'relevance', PAGE_SIZE, search, false)
    } finally {
      nextStartIndexRef.current = null
    }
  }, [currentCategoryInfo, fetchVariables, filteredVariables.length, isLoading, pagesLeft, search])

  const hasSearchQuery = search.trim().length > 0
  const bestMatchScore = useMemo(() => {
    if (!hasSearchQuery || filteredVariables.length === 0) {
      return null
    }

    const score = filteredVariables.reduce((bestScore, variable) => {
      return Math.max(bestScore, variable.matchScore ?? 0)
    }, 0)

    return Number.isFinite(score) && score > 0 ? score : null
  }, [filteredVariables, hasSearchQuery])
  const handleAskPrixAi = useCallback(
    async (question: string) => {
      await askPrixAi(buildVariableSearchQuestion(question))
    },
    [askPrixAi],
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <WrapperModalVariable>
        {!isMobile && (
          <WrapperFilters
            categoriesInfo={categoriesInfo}
            currentCategory={currentCategory}
            handleClick={handleClickCategory}
            setIsFilterOpen={setIsFilterOpen}
            isMobile={isMobile}
          />
        )}
        <div className='content'>
          <div className='filters'>
            <InputSearchStyled
              onSearchChange={handleSearch}
              setIsDebouncing={setIsDebouncing}
              placeholder='Buscar ou perguntar para a Prix IA...'
            />
            <div className='filter-category'>
              <ButtonVariants
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                icon={faList}
                variant='transparent'
                size='lg'
              >
                {isFilterOpen && (
                  <Modal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)}>
                    <WrapperModalVariable>
                      <WrapperFilters
                        categoriesInfo={categoriesInfo}
                        currentCategory={currentCategory}
                        handleClick={handleClickCategory}
                        setIsFilterOpen={setIsFilterOpen}
                        isMobile={isMobile}
                      />
                    </WrapperModalVariable>
                  </Modal>
                )}
              </ButtonVariants>
            </div>
          </div>
          <Handle isLoading={hasChangedVariableLayer || isDebouncing || (isLoading && !filteredVariables.length)}>
            {bestMatchScore !== null && (
              <VariableSearchMatchBanner
                bestMatchScore={bestMatchScore}
                question={search}
                onAsk={handleAskPrixAi}
                isLoading={isOpeningPrixAi}
              />
            )}
            <Variables>
              <ScrollList
                variables={filteredVariables}
                currentBaseLayer={currentBaseLayer}
                handleSelectVariable={handleSelectVariable}
                onLoadMoreItems={handleLoadMoreItems}
                nextPages={pagesLeft}
                apiIsLoading={isLoading}
                selectedVariableIsFilterableBy={filterableBy}
                getFilter={getFilter}
                filters={filters}
              />
              {filteredVariables.length === 0 && variables.length === 0 && (
                <NotFoundInfo>
                  {hasSearchQuery ? (
                    <AskPrixAiEmptyState
                      question={search}
                      onAsk={handleAskPrixAi}
                      isLoading={isOpeningPrixAi}
                    />
                  ) : (
                    <p>Sem resultados encontrados</p>
                  )}
                </NotFoundInfo>
              )}
            </Variables>
            <WrapperButtons>
              <ButtonVariants
                onClick={handleCancel}
                label='Cancelar'
                variant='transparent'
                size='sm'
              />
              <ButtonVariants
                onClick={handleConfirm}
                label='Confirmar'
                variant='primary'
                size='sm'
              />
            </WrapperButtons>
          </Handle>
        </div>
      </WrapperModalVariable>
    </Modal>
  )
}
